import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step5Review.module.css';

const API_ERROR_MAP = {
  'email-taken': { message: 'This email is already registered.', hint: 'Go back to Step 2 and change the email.', fixStep: 2, fixLabel: 'Edit Contact Info' },
  'email-invalid': { message: 'The email address is not valid.', hint: 'Go back to Step 2 and enter a valid email.', fixStep: 2, fixLabel: 'Edit Contact Info' },
  'password-too-short': { message: 'Password is too short — minimum 8 characters.', hint: 'Go back to Step 2.', fixStep: 2, fixLabel: 'Edit Contact Info' },
};

const getSignupErrorInfo = error => {
  if (!error) return null;
  const code = error?.apiErrors?.[0]?.code;
  const apiTitle = error?.apiErrors?.[0]?.title;
  if (code && API_ERROR_MAP[code]) return { ...API_ERROR_MAP[code], code };
  return { message: apiTitle || error?.message || 'Registration failed.', hint: null, fixStep: null, fixLabel: null, code };
};

const Row = ({ label, value }) =>
  value ? (
    <div className={css.row}>
      <span className={css.label}>{label}</span>
      <span className={css.value}>{value}</span>
    </div>
  ) : null;

const Section = ({ title, stepNum, onEdit, children }) => (
  <div className={css.section}>
    <div className={css.sectionHeader}>
      <span className={css.sectionTitle}>{title}</span>
      <button type="button" className={css.editBtn} onClick={() => onEdit(stepNum)}>✏ Edit</button>
    </div>
    <div className={css.sectionBody}>{children}</div>
  </div>
);

const AGREEMENT_LABELS = {
  buyerAgreement1: 'ChinaDolphins Buyer Agreement',
  buyerAgreement2: 'Privacy Policy',
  buyerAgreement3: 'Payment & Refund Terms',
  buyerAgreement4: 'Trade Compliance Declaration',
};

const formatAddress = (prefix, data) => {
  const parts = [
    data[`${prefix}Street`],
    data[`${prefix}City`],
    data[`${prefix}State`],
    data[`${prefix}PostalCode`],
    data[`${prefix}Country`],
  ].filter(Boolean);
  return parts.join(', ') || null;
};

const BuyerStep5Review = ({ prevStep, goToStep, formData, onSubmit, submitInProgress, submitError, className, rootClassName }) => {
  const classes = classNames(rootClassName || css.root, className);
  const [confirmed, setConfirmed] = React.useState(false);
  const [confirmError, setConfirmError] = React.useState(false);

  const acceptedCount = Object.keys(AGREEMENT_LABELS).filter(k => formData[k]).length;

  const handleSubmit = e => {
    e.preventDefault();
    if (!confirmed) { setConfirmError(true); return; }
    setConfirmError(false);
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={classNames(classes, css.form)}>

      <div className={css.header}>
        <span className={css.step}>Step 5 of 5</span>
        <h2 className={css.title}>Review & Submit</h2>
        <p className={css.description}>
          Review all your information below. Click <strong>Edit</strong> on any section to correct it.
        </p>
      </div>

      <hr className={css.divider} />

      {submitError && (() => {
        const errInfo = getSignupErrorInfo(submitError);
        return (
          <div className={css.apiError}>
            <div className={css.apiErrorIcon}>⚠</div>
            <div className={css.apiErrorBody}>
              <strong>Registration failed.</strong>
              <p className={css.apiErrorMsg}>{errInfo.message}</p>
              {errInfo.hint && <p className={css.apiErrorHint}>{errInfo.hint}</p>}
              <div className={css.apiErrorActions}>
                {errInfo.fixStep
                  ? <button type="button" className={css.goBackBtn} onClick={() => goToStep(errInfo.fixStep)}>{errInfo.fixLabel}</button>
                  : <button type="button" className={css.goBackBtn} onClick={() => goToStep(1)}>Edit Business Info</button>
                }
              </div>
            </div>
          </div>
        );
      })()}

      {/* Section 1 */}
      <Section title="Business Information" stepNum={1} onEdit={goToStep}>
        <Row label="Company Name" value={formData.companyName} />
        <Row label="Country" value={formData.country} />
        <Row label="Preferred Currency" value={formData.preferredCurrency} />
        <Row label="Tax / VAT Number" value={formData.taxNumber} />
        <Row label="Import / Export Details" value={formData.importExportDetails} />
        {!formData.companyName && <div className={css.missingWarning}>⚠ Business information is incomplete — please edit.</div>}
      </Section>

      {/* Section 2 */}
      <Section title="Contact & Account" stepNum={2} onEdit={goToStep}>
        <Row label="Contact Person" value={formData.contactPersonName} />
        <Row label="Email" value={formData.email} />
        <Row label="Phone" value={formData.phone} />
        {!formData.email && <div className={css.missingWarning}>⚠ Contact information is incomplete — please edit.</div>}
      </Section>

      {/* Section 3 */}
      <Section title="Addresses" stepNum={3} onEdit={goToStep}>
        <Row label="Shipping Address" value={formatAddress('shipping', formData)} />
        <Row
          label="Billing Address"
          value={formData.sameAsShipping ? 'Same as shipping address' : formatAddress('billing', formData)}
        />
        {!formData.shippingStreet && <div className={css.missingWarning}>⚠ Shipping address is incomplete — please edit.</div>}
      </Section>

      {/* Section 4 */}
      <Section title="Agreements" stepNum={4} onEdit={goToStep}>
        {Object.entries(AGREEMENT_LABELS).map(([key, label]) => (
          <div key={key} className={css.row}>
            <span className={css.label}>{label}</span>
            <span className={formData[key] ? css.green : css.red}>
              {formData[key] ? '✓ Accepted' : '✗ Not accepted'}
            </span>
          </div>
        ))}
        {acceptedCount < 4 && (
          <div className={css.missingWarning}>⚠ {4 - acceptedCount} agreement(s) not accepted — please edit.</div>
        )}
      </Section>

      {/* Pending notice */}
      <div style={{ background: 'linear-gradient(135deg,#fefce8,#fef9c3)', border: '1px solid #fde68a', borderRadius: 12, padding: '14px 18px', marginBottom: 20, fontSize: 13, color: '#92400e', lineHeight: 1.6 }}>
        <strong>📋 Pending Approval</strong><br />
        After submitting, your buyer account will be reviewed by the ChinaDolphins team.
        You will be notified by email once your account is approved for commercial activity.
      </div>

      {/* Confirm */}
      <label htmlFor="buyerConfirmCheck" style={{
        display: 'flex', alignItems: 'flex-start', gap: 14, padding: '18px 20px', borderRadius: 12,
        border: `1.5px solid ${confirmError ? '#ef4444' : confirmed ? '#2563eb' : '#e2e8f0'}`,
        background: confirmed ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : confirmError ? '#fff5f5' : '#f8fafc',
        cursor: 'pointer', marginBottom: 6, transition: 'all 0.2s',
        boxShadow: confirmed ? '0 2px 8px rgba(37,99,235,0.1)' : 'none',
      }}>
        <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${confirmed ? '#2563eb' : confirmError ? '#ef4444' : '#cbd5e1'}`, background: confirmed ? '#2563eb' : '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, transition: 'all 0.18s' }}>
          {confirmed && (
            <svg width="13" height="10" viewBox="0 0 13 10" fill="none">
              <path d="M1.5 5L5 8.5L11.5 1.5" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
        <input type="checkbox" id="buyerConfirmCheck" checked={confirmed} onChange={e => { setConfirmed(e.target.checked); if (e.target.checked) setConfirmError(false); }} style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
        <div style={{ fontSize: 14, lineHeight: 1.6, color: confirmed ? '#1d4ed8' : '#334155', fontWeight: confirmed ? 600 : 400 }}>
          I confirm that all the information provided is accurate and I am authorised to register this business account on ChinaDolphins.
          <span style={{ color: '#ef4444' }}> *</span>
        </div>
      </label>
      {confirmError && <div style={{ fontSize: 12, color: '#ef4444', fontWeight: 600, marginBottom: 12, paddingLeft: 4 }}>⚠ Please confirm before submitting.</div>}

      <div className={css.buttonContainer}>
        <button type="button" onClick={prevStep} className={css.previous}>← Previous</button>
        <button type="submit" className={classNames(css.submit, { [css.submitDisabled]: submitInProgress })} disabled={submitInProgress}>
          {submitInProgress ? 'Submitting…' : 'Submit Application →'}
        </button>
      </div>

    </form>
  );
};

BuyerStep5Review.defaultProps = { className: null, rootClassName: null, formData: {}, submitInProgress: false, submitError: null };
BuyerStep5Review.propTypes = {
  className: PropTypes.string,
  rootClassName: PropTypes.string,
  formData: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  submitInProgress: PropTypes.bool,
  submitError: PropTypes.object,
};

export default BuyerStep5Review;
